import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import LoadingSpinner from 'components/loading-spinner';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { isValidDecimal, isValidInteger, isValidMeanWeight } from 'utils/validate-numbers';
import { getTransfer, putTransfer } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import {
  UpdateTransferCycleInput,
  UpdateTransferInput,
} from 'views/production-systems/features/registration-log/types/update-transfer-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { Transfer } from 'views/production-systems/types/transfer.type';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../components/registration-header';

type Props = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type SourceRow = {
  logId: number;
  cycleId: number;
  cycleName: string;
  pondId: string;
  pondName: string;
  currentQty: number;
  currentMw: number;
  currentBm: number;
  remainingQty: number;
  remainingMw: number;
  remainingBm: number;
  newCycleName: string;
  nextCycleId: number;
};

type DestinationRow = {
  pondName: string;
  pondId: string;
  cycleId: number;
  previousCycleId: number;
  cycleName: string;
  currentQty: number;
  transferQty: number;
  transferMw: number;
  transferBm: number;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.transfer.edit';

const EditTransfer: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const sourceDataGridApiRef = useGridApiRef();
  const snackbarDispatch = useSnackbarDispatch();
  const destinationDataGridApiRef = useGridApiRef();
  const { lockedPonds, releasePonds, globalSiteId, lockPonds } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  // #region STATES
  const [confirm, setConfirm] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);
  const [isPartialTransfer, setIsPartialTransfer] = useState<boolean>(false);
  const [validateTransferNumber, setValidateTransferNumber] = useState<boolean>(false);

  //#endregion STATES

  //#region FETCH DATA

  const { data: transfer, isFetching } = useGet(
    ['getTransfer', globalSiteId, logId, eventNr],
    ({ queryKey }) => getTransfer(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  useEffect(() => {
    if (!transfer) return;

    if (canEdit) {
      const ponds = transfer.toCycles.length ? transfer.toCycles.map((destination) => destination.pondId) : [];
      ponds.push(String(transfer.fromCycle.pondId));
      lockPonds(ponds);
    }
    setIsPartialTransfer(transfer.fromCycle.isPartialTransfer);
  }, [canEdit, lockPonds, transfer]);

  // #region POST DATA
  const updateTransfer = usePost<Transfer, { globalSiteId: string; data: UpdateTransferInput }>(
    (request) => putTransfer(request.globalSiteId, request.data),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      let pondIds = response.toCycles.reduce((prev, curr) => [...prev, curr.pondId], [response.fromCycle.pondId]);

      transfer && releasePonds(pondIds);
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#region Source Data Grid

  const [sourceColumnVisibility, setSourceColumnVisibility] = useState<GridColumnVisibilityModel>({
    pondName: true,
    pondId: true,
    cycleName: true,
    currentQty: true,
    currentMw: true,
    currentBm: true,

    newCycleName: !isPartialTransfer,
    remainingQty: !isPartialTransfer,
    remainingMw: !isPartialTransfer,
    remainingBm: !isPartialTransfer,
  });

  useEffect(() => {
    if (!!sourceDataGridApiRef.current) {
      setSourceColumnVisibility({
        ...sourceColumnVisibility,
        newCycleName: isPartialTransfer,
        remainingQty: isPartialTransfer,
        remainingMw: isPartialTransfer,
        remainingBm: isPartialTransfer,
      });
    }
  }, [isPartialTransfer, sourceDataGridApiRef]);

  const [sourceRow, setSourceRow] = useState<SourceRow>({
    logId: logId,
    cycleId: 0,
    pondId: '',
    pondName: '',
    cycleName: '',
    currentQty: 0,
    currentMw: 0,
    currentBm: 0,
    remainingQty: 0,
    remainingMw: 0,
    newCycleName: '',
    nextCycleId: 0,
    remainingBm: 0,
  });

  const [destinationRows, setDestinationRows] = useState<readonly DestinationRow[]>([]);

  useEffect(() => {
    if (!!transfer) {
      const sourcePond = transfer.fromCycle;

      let row = {
        logId: logId,
        cycleId: sourcePond.id,
        cycleName: sourcePond.previousCycleName,
        pondId: sourcePond.pondId,
        pondName: sourcePond.pondName,
        currentQty: sourcePond.previousQty,
        currentMw: sourcePond.previousMw,
        currentBm: calculateBiomass(sourcePond.previousMw, sourcePond.previousQty),
        remainingQty: sourcePond.remainingQty,
        remainingMw: sourcePond.remainingMw,
        newCycleName: sourcePond.newCycleName,
        nextCycleId: sourcePond.nextCycleId,
        remainingBm: calculateBiomass(sourcePond.remainingMw, sourcePond.remainingQty),
      };

      let rows: readonly any[] = transfer.toCycles.map((cycle) => {
        return {
          previousCycleId: cycle.previousCycleId,
          cycleId: cycle.id,
          pondName: cycle.pondName,
          pondId: cycle.pondId,
          cycleName: cycle.name || `${cycle.pondName}_${cycle.id}_${dayjs(transfer.date).format('L')}`,
          currentQty: cycle.previousQty,
          transferQty: cycle.transferQty,
          transferMw: cycle.transferMw,
          transferBm: calculateBiomass(cycle.transferMw, cycle.transferQty),
        };
      });

      setSourceRow(row);
      setDestinationRows(rows);
    }
  }, [transfer]);

  const processRowUpdate = (newRow: GridRowModel<SourceRow>) => {
    const updatedRow = { ...newRow, remainingBm: calculateBiomass(newRow.remainingMw, newRow.remainingQty) };
    setSourceRow((row) => (row.pondId === newRow.pondId ? updatedRow : row));
    return updatedRow;
  };

  const sourceColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.sourcePond` }),
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'cycleName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.cycleName` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'currentQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.currentQty` }),
        editable: false,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidInteger(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-currentQty`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'currentMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.currentMw` }),
        type: 'number',
        editable: false,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-currentMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'currentBm',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.currentBm` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-currentBm`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'newCycleName',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.newCycleName` }),
        flex: 1,
        type: 'string',
        editable: !readOnly,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value === '';
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-newCycleName`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'remainingQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.remainingQty` }),
        flex: 1,
        type: 'string',
        align: 'right',
        headerAlign: 'right',
        editable: true,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidInteger(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingQty`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'remainingMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.remainingMw` }),
        editable: !readOnly,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'remainingBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.source.remainingBm` }),
        flex: 1,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        editable: false,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingBm`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
    ],
    [readOnly]
  );

  //#endregion

  //#region Destination Data Grid

  const destinationProcessRowUpdate = (newRow: GridRowModel<DestinationRow>) => {
    const updatedRow = { ...newRow, transferBm: calculateBiomass(newRow.transferMw, newRow.transferQty) };
    setDestinationRows(destinationRows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const destinationColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destination.destinationPond` }),
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'cycleName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destination.cycleName` }),
        editable: !readOnly,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'currentQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destination.currentQty` }),
        editable: false,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'transferQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destination.transferQty` }),
        type: 'number',
        editable: !readOnly,
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-transferQty`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'transferMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destination.transferMw` }),
        editable: !readOnly,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value) || !isValidMeanWeight(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-transferMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'transferBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destination.transferBm` }),
        flex: 1,
        type: 'number',
        editable: false,
        align: 'right',
        headerAlign: 'right',
      },
    ],
    [readOnly]
  );

  useEffect(() => {
    if (isPartialTransfer && transfer?.fromCycle) {
      const qty = destinationRows.reduce((prev, curr) => prev + curr.transferQty, 0);
      const previousQty = transfer.toCycles.reduce((prev, curr) => prev + curr.transferQty, 0);

      const newRemainingQty = transfer.fromCycle.remainingQty - (qty - previousQty);
      setSourceRow((row) => ({
        ...row,
        remainingQty: newRemainingQty,
        remainingBm: calculateBiomass(row.remainingMw, newRemainingQty),
      }));
    }
  }, [destinationRows, isPartialTransfer]);

  const summaryValues: SummaryItem[] = useMemo(() => {
    let summary = destinationRows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.transferMw, curr.transferQty);

        return {
          transferQty: Number(prev.transferQty) + Number(curr.transferQty),
          transferMw: 0,
          transferBm: Number(prev.transferBm) + biomass,
        };
      },
      {
        transferQty: 0,
        transferMw: 0,
        transferBm: 0,
      }
    );

    summary.transferMw = calculateMeanWeight(summary.transferBm, summary.transferQty);

    return [
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: summary.transferQty,
        type: 'number',
        hidden: false,
      },
      {
        value: summary.transferMw,
        type: 'number',
        hidden: false,
        minimumFractionDigits: 2,
      },
      {
        value: summary.transferBm,
        type: 'number',
        hidden: false,
        minimumFractionDigits: 2,
      },
    ];
  }, [destinationRows]);

  //#endregion

  // #region HANDLERS

  const handleSubmit = () => {
    let destinationRowsData: UpdateTransferCycleInput[] = destinationRows.map((row) => ({
      cycleId: row.cycleId,
      previousCycleId: row.previousCycleId,
      pondId: row.pondId,
      transferQty: row.transferQty,
      transferMw: row.transferMw,
      cycleName: row.cycleName,
    }));

    let data: UpdateTransferInput = {
      cycleId: sourceRow.cycleId,
      logId: sourceRow.logId,
      pondId: sourceRow.pondId,
      endQty: sourceRow.currentQty,
      endMw: sourceRow.currentMw,
      remainingQty: sourceRow.remainingQty,
      remainingMw: sourceRow.remainingMw,
      newCycleName: sourceRow.newCycleName,
      nextCycleId: sourceRow.nextCycleId,

      toCycles: destinationRowsData,
    };

    console.log({ data, transfer });

    updateTransfer.mutate({
      globalSiteId: globalSiteId,
      data: data,
    });
  };

  //#region HOOKS

  useEffect(() => {
    if (canEdit) {
      if (lockedPonds.notGranted.length > 0 && confirm) {
        setReadOnly(true);
      } else {
        setReadOnly(false);
      }
    }
  }, [confirm, canEdit, lockedPonds.notGranted]);

  const disableSubmit = useMemo(() => {
    return destinationRows.some((row) => {
      if (isPartialTransfer) {
        destinationRows.map((row) => {
          return !isValidInteger(row.currentQty) && !isValidDecimal(row.transferMw);
        });
      }
    });
  }, [destinationRows]);

  //#endregion HOOKS

  return !!lockedPonds?.notGranted.length && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : isFetching ? (
    <LoadingSpinner />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      <Box display={'flex'} flexDirection={'column'} gap={8}>
        <RegistrationHeader dateCreated={dateCreated} />
        <Box>
          <Box flex={1} display={'flex'} flexDirection={'row'} gap={1}>
            <FormattedMessage id={`productionSystems.farmOverview.transfer.sourcePond`} />
            <Typography>
              (
              {isPartialTransfer ? (
                <FormattedMessage id={`productionSystems.farmOverview.transfer.edit.source.partialTransfer`} />
              ) : (
                <FormattedMessage id={`productionSystems.farmOverview.transfer.edit.source.fullTransfer`} />
              )}
              )
            </Typography>
          </Box>
          <DataGrid
            autoHeight
            hideFooter
            rows={[sourceRow]}
            columns={sourceColumns}
            apiRef={sourceDataGridApiRef}
            onCellEditStop={handleEditing}
            getRowId={(row) => row.pondId}
            processRowUpdate={processRowUpdate}
            columnVisibilityModel={sourceColumnVisibility}
            onRowClick={({ id }) => {
              const { field } = sourceDataGridApiRef.current?.state.tabIndex.cell ?? {};
              sourceDataGridApiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
          />
        </Box>
        <Box>
          {validateTransferNumber && (
            <Alert severity="warning" sx={{ mb: 2, width: { xs: 'auto', md: '50%' } }}>
              <FormattedMessage id={intl.formatMessage({ id: `${PAGE_NAME_ID}.transferNumberError` })} />
            </Alert>
          )}
          <FormattedMessage id={`productionSystems.farmOverview.transfer.destinationPonds`} />
          <DataGrid
            autoHeight
            rows={destinationRows}
            columns={destinationColumns}
            onCellEditStop={handleEditing}
            getRowId={(row) => row.pondId}
            apiRef={destinationDataGridApiRef}
            isCellEditable={(params) => !!params.colDef.editable}
            processRowUpdate={destinationProcessRowUpdate}
            showToolbar
            slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${intl.formatMessage({
                    id: 'productionSystems.registrationLog.registrationTypes.transfer',
                  })}-${dayjs().format('DD/MM/YYYY')}`,
                },
              },
              footer: {
                summaryValues: summaryValues,
              } as DataGridSummaryProps,
            }}
            onRowClick={({ id }) => {
              const { field } = destinationDataGridApiRef.current?.state.tabIndex.cell ?? {};
              sourceDataGridApiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
          />
        </Box>
      </Box>
      {!readOnly && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            disabled={disableSubmit || updateTransfer.isPending || isEditing || hasError}
            onClick={handleSubmit}
            startIcon={updateTransfer.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            <FormattedMessage id={'general.confirm'} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EditTransfer);
